import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColumnDefinition } from "tabulator-tables";
import { Preferences } from "../../api/account/Preferences";
import { Users } from "../../api/account/Users";
import { dataInjector } from "../../api/compute/commons";
import { Instruments } from "../../api/compute/Instruments";
import { Lists } from "../../api/compute/Lists";
import { Strategies } from "../../api/compute/Strategies";
import { Structures } from "../../api/compute/Structures";
import { SystematicProducts } from "../../api/compute/SystematicProducts";
import { Utils } from "../../api/compute/Utils";
import Tabulator, { EventsType } from "../../components/Tabulator";
import { deepClone } from "../../deepClone";
import { useAuth } from "../../hooks/useAuth";
import { httpAll } from "../../httpAll";
import { TDate } from "../../trendrating/date/TDate";
import { Formatter } from "../../trendrating/Formatter";
import { widgetsConfiguration } from "../../WidgetConf";
import { useFeedbackSystem } from "../../hooks/useFeedbackSystem";

export const _prepareThresholdValue = (threshold) => {
  var _threshold = parseFloat(threshold);
  _threshold = _threshold === 0 ? 0.0001 : _threshold;
  return _threshold;
};

export default function AutoRebalanceNotifier() {
  const auth = useAuth();
  const smsAPI = useMemo(() => new SystematicProducts(), []);
  const utilsAPI = useMemo(() => new Utils(), []);
  const strategyAPI = useMemo(() => new Strategies(auth.user35), [auth.user35]);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  const listAPI = useMemo(() => new Lists(auth.user35), [auth.user35]);
  const instrumentsAPI = useMemo(() => new Instruments(), []);
  const structuresAPI = useMemo(() => new Structures(), []);
  const userApi = useMemo(() => new Users(), []);
  const formatter = useMemo(() => new Formatter(), []);
  const [data, setData] = useState<any>(null);
  const preferenceAPI = useMemo(() => new Preferences(), []);
  const feedback = useFeedbackSystem();
  const [report, setReport] = useState<any>(null);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    formatter.initFormatter();
  }, [formatter]);

  //#region - utility functions

  const listenerExportOrders = useCallback((_data) => {
    if (_data == null) {
      return;
    }
    var data = _data["diff"];
    var lines = [
      [
        // "ISIN",
        "Ticker",
        "Name",
        // "Rating",
        "Currency",
        "Market",
        // "Mkt. Cap.",
        "Action",
        "Quantity",
      ],
    ];
    var line: any = null;
    var position: any = null;
    for (let i = 0, length = data.length; i < length; i++) {
      line = [];
      position = data[i];

      // line.push(position["isin"]);
      line.push(position["ticker"]);
      line.push(position["name"]);
      // line.push(
      //   formatter.text(
      //     "rc",
      //     "table",
      //     position["rc"],
      //     position,
      //     position["type"]
      //   )
      // );
      line.push(position["currency"]);
      line.push(position["country"]);
      // line.push(
      //   formatter.text(
      //     "numberBigFmt",
      //     "table",
      //     position["marketcap"],
      //     position,
      //     position["type"]
      //   )
      // );
      line.push(
        _formatAction("TEXT", position["_synthAction"], {
          c_position_buy: "Buy",
          c_position_decrease: "Decrease",
          c_position_increase: "Increase",
          c_position_sell: "Sell",
        })
      );
      line.push(position["_synthActionValue"]);
      // line.push(
      //   formatter.number({
      //     options: {
      //       hasPositiveSign: true,
      //       isPercentage: true,
      //       notAvailable: {
      //         input: null,
      //         output: "",
      //       },
      //     },
      //     output: "TEXT",
      //     value: position["_synthActionValue"],
      //     valueHelper: null,
      //   })
      // );

      lines.push(line);
    }
    return lines;
  }, []);

  const listenerExportNewAllocation = useCallback((_data) => {
    var data = deepClone(_data["last"]);

    var lines = [
      [
        "Weight",
        // "ISIN",
        "Ticker",
        "Name",
        // "Rating",
        "Currency",
        "Market",
        // "Mkt. Cap.",
      ],
    ];

    var line: any = null;
    var position: any = null;
    for (let i = 0, length = data.length; i < length; i++) {
      line = [];
      position = data[i];

      line.push(position["weight"]);

      // line.push(
      //   formatter.text(
      //     "weight",
      //     "table",
      //     position["weight"],
      //     position,
      //     position["type"]
      //   )
      // );

      // line.push(position["isin"]);
      line.push(position["ticker"]);
      line.push(position["name"]);
      // line.push(
      //   formatter.text(
      //     "rc",
      //     "table",
      //     position["rc"],
      //     position,
      //     position["type"]
      //   )
      // );
      line.push(position["currency"]);
      line.push(position["country"]);
      // line.push(
      //   formatter.text(
      //     "numberBigFmt",
      //     "table",
      //     position["marketcap"],
      //     position,
      //     position["type"]
      //   )
      // );

      lines.push(line);
    }
    return lines;
  }, []);

  const _dataPrepare = useCallback(
    async (dataCompare, response) => {
      var dataLast = dataInjector(
        response["last"]["data"],
        dataCompare["result"]["positions"],
        ["weight"]
      );
      var position: any = null;
      let taxonomies = await structuresAPI.taxonomies();
      for (let i = 0, length = dataLast.length; i < length; i++) {
        position = dataLast[i];
        // replace ICB code with string to enable dgrid native sort
        // avoiding calls to server
        position["_synthWhat"] = formatter.taxon({
          options: {
            ancestorAtLevel: "1 Industry",
            notAvailable: {
              input: null,
              output: "",
            },
            taxonomy: taxonomies["ICB"],
          },
          output: "TEXT",
          value: position["icb"],
          valueHelper: null,
        });
      }
      var dataDiff: any = response["diff"]["data"];
      var diffMap: any = {};
      for (let i = 0; i < dataDiff.length; i++) {
        position = dataDiff[i];
        // enriching dataDiff with synthetic fields
        position["_synthAction"] = null;
        position["_synthActionValue"] = null;
        diffMap[position["symbol"]] = i;
      }
      var difference = dataCompare["diff"];
      var index: any = null;
      for (let i = 0; i < difference.length; i++) {
        position = difference[i];
        index = diffMap[position["symbol"]];
        if (index != null) {
          dataDiff[index]["_synthActionValue"] = position["delta"];
          if (position["from"] === 0 && position["to"] > 0) {
            dataDiff[index]["_synthAction"] = "buy";
          } else if (position["from"] > 0 && position["to"] === 0) {
            dataDiff[index]["_synthAction"] = "sell";
          } else if (position["delta"] > 0) {
            dataDiff[index]["_synthAction"] = "increase";
          } else {
            dataDiff[index]["_synthAction"] = "decrease";
          }
        }
      }
      return {
        diff: dataDiff,
        last: dataLast,
        turnover: dataCompare["turnover"],
      };
    },
    [formatter, structuresAPI]
  );

  const _dataGetRebalanceResult = useCallback(
    async (response, snapshotOfProduct, product) => {
      var data = deepClone(response);
      /* Data cleanup, removing diff symbols with delta 0 */
      var newDiff: any[] = [];
      if (data["diff"] != null) {
        for (let i = 0; i < data["diff"].length; i++) {
          if (data["diff"][i]["delta"] !== 0) {
            newDiff.push(data["diff"][i]);
          }
        }
        data["diff"] = newDiff;
      }
      // last allocation
      var propertiesLast =
        widgetsConfiguration["widgets/systematic-product/rebalance/last"][
          "properties"
        ];
      var symbols: any[] = [];
      var positions = data["result"]["positions"];
      for (let i = 0; i < positions.length; i++) {
        symbols.push(positions[i]["symbol"]);
      }
      var reqLast = instrumentsAPI.fetch({
        properties: propertiesLast,
        symbols: symbols,
        type: "security",
      });
      // difference (rebalance orders)
      var propertiesDiff =
        widgetsConfiguration["widgets/systematic-product/rebalance/diff"][
          "properties"
        ];
      symbols = [];
      positions = data["diff"];
      for (let i = 0; i < positions.length; i++) {
        symbols.push(positions[i]["symbol"]);
      }
      var reqDiff = instrumentsAPI.fetch({
        properties: propertiesDiff,
        symbols: symbols,
        type: "security",
      });
      // Statistics from target portfolio
      positions = data["result"]["positions"];
      var pos: any[] = [];
      for (let i = 0; i < positions.length; i++) {
        pos.push({
          S: positions[i].symbol,
          A: positions[i].weight,
        });
      }
      
      var params = {
        diff: reqDiff,
        last: reqLast,
        strategy: product.strategy /*pp.getStrategy()*/,
      };
      const dataCompare = await httpAll(params);
      return await _dataPrepare(data, dataCompare);
    },
    [_dataPrepare, instrumentsAPI]
  );

  const _dataGetCompare = useCallback(
    async (rebalancedProduct, snapshotOfProduct, product) => {
      var threshold = _prepareThresholdValue(product.orderThreshold);
      var paramsCompare = {
        source: {
          name: "Snapshot",
          positions: snapshotOfProduct["positions"] ?? [],
        },
        target: {
          name: "Last rebalance",
          positions: rebalancedProduct["positions"],
        },
        threshold: threshold,
        thresholdInOut: threshold,
      };
      const listCompareResponse = await listAPI.compare(paramsCompare);
      return await _dataGetRebalanceResult(
        listCompareResponse,
        snapshotOfProduct,
        product
      );
    },
    [_dataGetRebalanceResult, listAPI]
  );

  //#endregion

  const getData = useCallback(
    async (todayFromServer) => {
      try {
        setIsFetchingData(true);
        //#region - select active users
        const activeUsers = await userApi.select({
          searches: [{ filters: [{ dimension: "active", segments: [1] }] }],
          sort: { dimension: "active", rev: false },
        });
        //#endregion

        //#region - select and fetch preferences of active users
        let generalPreferences = await preferenceAPI.select(activeUsers);
        generalPreferences = await preferenceAPI.fetch(generalPreferences, [
          "object.preferences",
          "ownerId",
        ]);
        generalPreferences = generalPreferences.data[0].rows;

        //#endregion

        //#region - if a user want to be notified keep his id
        const usersThatWantToBeNotified: number[] = [];
        generalPreferences.forEach((obj) => {
          //! undefined/null/true means that it will receive the notification
          if (
            obj.preferences?.emailDigest
              ?.autoRebalanceSystematicPortfolioNotification === true
          ) {
            usersThatWantToBeNotified.push(obj.ownerId);
          }
        });
        //#endregion

        //#region - select the sms of users that wants to be notified
        const sms_users_IDS = await smsAPI.select({
          searches: [
            {
              filters: [
                { dimension: "autorebalance", segments: [true] },
                {
                  dimension: "ownerId",
                  segments: usersThatWantToBeNotified,
                },
              ],
            },
          ],
          sort: { dimension: "autorebalance", rev: false },
        });
        //#endregion

        //#region - select user 35 sms
        const sms_35_IDS = await smsAPI.select({
          searches: [
            {
              filters: [
                { dimension: "autorebalance", segments: [true] },
                {
                  dimension: "ownerId",
                  segments: [35],
                },
              ],
            },
          ],
          sort: { dimension: "autorebalance", rev: false },
        });
        //#endregion

        //#region - fetch sms properties
        let smsInfo: any[] = await smsAPI.fetch({
          ids: [...sms_users_IDS, ...sms_35_IDS],
          properties: ["ownerId", "calendarRebalance", "name"],
        });
        //#endregion

        //#region - filter systematic portfolios that have been rebalanced today
        smsInfo = smsInfo.filter(
          (info) => info.calendarRebalance.rebalance === todayFromServer
        );
        //#endregion

        //#region - create map {userid: {systematicPortfolios:[], userData: {}}}
        const mappa = {};
        let subscribableSystematicPortfolios = smsInfo.filter(
          (SPs) => SPs.ownerId === 35
        );
        if (subscribableSystematicPortfolios.length > 0) {
          for (const item of subscribableSystematicPortfolios) {
            let relations = await utilsAPI.relations({
              action: "unpublish",
              item: {
                id: item.id,
                isSubscribed: false,
                type: "SYSTEMATIC_PRODUCT",
                pubSubType: "systematicPortfolio",
              },
              user: { id: 35 },
            });
            relations = relations.data.relations;

            if (relations.length > 0) {
              relations.forEach((relation) => {
                if (mappa[relation[0].userId]?.systematicPortfolios) {
                  mappa[relation[0].userId].systematicPortfolios.push({
                    id: item.id,
                    name: item.name,
                    isReadOnly: true,
                  });
                } else {
                  mappa[relation[0].userId] = {
                    systematicPortfolios: [
                      {
                        id: item.id,
                        name: item.name,
                        isReadOnly: true,
                      },
                    ],
                  };
                }
              });
            }
          }
        }

        const notsubscribableSystematicPortfolios = smsInfo.filter(
          (SPs) => SPs.ownerId !== 35
        );

        for (const SP of notsubscribableSystematicPortfolios) {
          if (mappa[SP.ownerId]?.["systematicPortfolios"]) {
            mappa[SP.ownerId]["systematicPortfolios"].push({
              id: SP.id,
              name: SP.name,
              isReadOnly: false,
            });
          } else {
            mappa[SP.ownerId] = {
              systematicPortfolios: [
                {
                  id: SP.id,
                  name: SP.name,
                  isReadOnly: false,
                },
              ],
            };
          }
        }

        // if user 35 doesn't want to be notified, remove it from mappa
        if (!usersThatWantToBeNotified.includes(35)) {
          if (mappa[35]) {
            delete mappa[35];
          }
        }

        // after relations a user that wont receive notification
        // may be in mappa. we check again to remove them in case
        Object.keys(mappa).forEach((id) => {
          const _id = parseInt(id);
          if (!usersThatWantToBeNotified.includes(_id)) {
            delete mappa[_id];
          }
        });

        //get user Data for each user
        let userIDS: number[] = Object.keys(mappa).map((id) => parseInt(id));
        let userData = await userApi.get({
          ids: userIDS,
          properties: ["email", "firstName", "lastName", "level"],
        });
        userData = userData.data[0].rows;

        if (userData.length > 0) {
          for (let i = 0; i < userData.length; i++) {
            mappa[userData[i].id]["userData"] = userData[i];
          }
        }
        //#endregion

        setData(mappa);
        setIsFetchingData(false);
      } catch (error: any) {
        alert(error.message);
        console.log(error);
        setIsFetchingData(false);
      }

      //get the owners of the SPs and if owner is 35 get the subscribers of the user's 35 SPs
    },
    [preferenceAPI, smsAPI, userApi, utilsAPI]
  );

  useEffect(() => {
    utilsAPI.today().then(
      (response) => {
        const _today = response?.today;
        if (_today) {
          getData(response?.today);
        } else {
          throw new Error("Could not retrieve today date from server!");
        }
      },
      (error) => {
        alert(error.message);
        console.log(error);
      }
    );
  }, [getData, utilsAPI]);

  const tableRef = useRef<any>(null);
  const [tableReady, setTableReady] = useState(false);
  const [sendBtnDisabled, setSendBtnDisabled] = useState(true);
  const tableEvents: EventsType = useMemo(
    () => [
      {
        event: "tableBuilt",
        callback: () => {
          setTableReady(true);
        },
      },
      {
        event: "tableDestroyed",
        callback: () => {
          setTableReady(false);
        },
      },
      {
        event: "rowSelectionChanged",
        callback: function (e, rows) {
          if (rows.length > 0) {
            setSendBtnDisabled(false);
          } else {
            setSendBtnDisabled(true);
          }
        },
      },
    ],
    []
  );
  const columns: ColumnDefinition[] = useMemo(
    () => [
      {
        formatter: "rowSelection",
        titleFormatter: "rowSelection",
        title: "",
        resizable: false,
        frozen: true,
        widthGrow: 0.3,
        hozAlign: "center",
        headerHozAlign: "center",
        headerSort: false,
      },
      {
        title: "ID",
        field: "id",
        align: "center",
        hozAlign: "center",
        widthGrow: 0.5,
      },
      {
        title: "First Name",
        field: "firstName",
        widthGrow: 1,
        hozAlign: "left",
      },
      { title: "Last Name", field: "lastName", widthGrow: 1, hozAlign: "left" },
      { title: "Email", field: "email", widthGrow: 2, hozAlign: "left" },
      { title: "Type", field: "level", widthGrow: 1, hozAlign: "left" },

      {
        title: "Attachments",
        field: "attachments",
        hozAlign: "center",
        headerHozAlign: "center",
        widthGrow: 0.3,
        formatter: (cell) => {
          const cellValue = cell
            .getRow()
            .getCell("systematicPortfolios")
            .getValue();
          const attachmentNumber: number = cellValue.length;
          return attachmentNumber.toString();
        },
      },
      {
        title: "Systematic Portfolios",
        field: "systematicPortfolios",

        widthGrow: 4,
        formatter: (cell) => {
          const cellValue = cell.getValue();
          const names = cellValue.map((sp) => sp.name).join(", ");
          return names;
        },
      },
    ],
    []
  );
  const tableOptions = useMemo(
    () => ({
      columns: columns,
      layout: "fitColumns",
      selectableRows: true,
    }),
    [columns]
  );

  const getOrdersAndAllocations = useCallback(
    async (smsIds: any[]) => {
      const systematicData: any[] = await smsAPI.fetch({
        ids: smsIds,
        properties: [
          "positionsToday",
          "strategyId",
          "historicalPortfolioId",
          "name",
          "ownerId",
          "orderThreshold",
        ],
      });

      const obj = {}; // {sms_id:{orders,allocations}}

      for (const sytematicPortfolio of systematicData) {
        const strategyID = sytematicPortfolio.strategyId;
        const _product = deepClone(sytematicPortfolio);
        if (_product?.orderThreshold === undefined) {
          _product.orderThreshold = 0;
        }
        const historicalPortfolio = await smsAPI.listHistory({
          id: _product.historicalPortfolioId,
        });

        const _strategy = await strategyAPI.get(strategyID);
        let THETODAYDATE: any = await utilsAPI.today();
        let THEPOSITIONTODAY = _product?.["positionsToday"]?.[0];
        if (THEPOSITIONTODAY) {
          THEPOSITIONTODAY.d = THETODAYDATE.today;
        } else {
          THEPOSITIONTODAY = {};
          THEPOSITIONTODAY["d"] = THETODAYDATE.today;
          THEPOSITIONTODAY["v"] = [];
        }

        let test: any = null;
        let THEPREVIOUSPORTFOLIO =
          historicalPortfolio.POS[historicalPortfolio.POS.length - 2];
        if (!THEPREVIOUSPORTFOLIO) {
          THEPREVIOUSPORTFOLIO = {};
          THEPREVIOUSPORTFOLIO["d"] = THETODAYDATE.today;
          THEPREVIOUSPORTFOLIO["v"] = [];
        } else {
          //#region - drifting the portfolio
          const idAndName =
            "auto-generated-systematic-portfolio" + new Date().getTime();
          const list = Lists.getPrototype();
          list.name = idAndName;
          list.type = "PORTFOLIO";
          list.weightsManagement = {
            currency: historicalPortfolio.currency,
            date: THEPREVIOUSPORTFOLIO.d,
            type: "drifting",
          } as any;
          list.positions = THEPREVIOUSPORTFOLIO.v.map((vPos: any) => ({
            symbol: vPos.S,
            weight: vPos.A,
          }));

          test = await listAPI._updateWeights(
            list.positions,
            list.weightsManagement.currency,
            TDate.daysToIso8601(THEPREVIOUSPORTFOLIO.d),
            TDate.daysToIso8601(THETODAYDATE.today)
          );
          THEPREVIOUSPORTFOLIO.v = test.v;
          //#endregion
        }

        THEPREVIOUSPORTFOLIO = smsAPI._rebalance(THEPREVIOUSPORTFOLIO);
        THEPOSITIONTODAY = smsAPI._rebalance(THEPOSITIONTODAY);
        const datum = await _dataGetCompare(
          THEPOSITIONTODAY,
          THEPREVIOUSPORTFOLIO,
          {
            ..._product,
            strategy: _strategy,
          }
        );

        let orders: any = listenerExportOrders(datum);
        let orders_header = orders[0];
        let orders_rows = orders.slice(1);
        orders_rows.sort((a, b) => {
          if (a[4] < b[4]) return -1;
          if (a[4] > b[4]) return 1;
          // Se l'Action è la stessa, ordina per Quantity in ordine crescente
          return a[5] - b[5];
        });
        orders = [orders_header, ...orders_rows];

        let allocations: any = listenerExportNewAllocation(datum);
        let allocations_header = allocations[0];
        let allocations_rows = allocations.slice(1);
        allocations_rows.sort((a, b) => {
          if (a[4] < b[4]) return -1;
          if (a[4] > b[4]) return 1;
          // Se Market è lo stesso, ordina per weight in ordine crescente
          return a[0] - b[0];
        });
        allocations = [allocations_header, ...allocations_rows];

        obj[_product.id] = { orders, allocations };
      }

      return obj;
    },
    [
      _dataGetCompare,
      listAPI,
      listenerExportNewAllocation,
      listenerExportOrders,
      smsAPI,
      strategyAPI,
      utilsAPI,
    ]
  );

  const sendNotificationHandler = useCallback(async () => {
    if (tableRef.current) {
      const table = tableRef.current.getTableInstance();
      if (table.getSelectedData().length < 1) {
        alert("Please select a user to send notifications");
        return;
      }
      setIsSendingEmails(true);
      setShowReport(false);
      setReport(null);
      const selectedUsers = table
        .getSelectedData()
        .map((user) => parseInt(user.id));
      let _data: any = Object.entries(data).filter((entry) =>
        selectedUsers.includes(parseInt(entry[0]))
      );
      _data = Object.fromEntries(_data);
      const SMS_ID_SET = new Set<number>();
      Object.keys(_data).forEach((userId) => {
        const systematicPortfolios: any[] = _data[userId].systematicPortfolios;
        systematicPortfolios.forEach((sms) => {
          SMS_ID_SET.add(sms.id);
        });
      });

      try {
        const SMS_BUFFER = await getOrdersAndAllocations(
          Array.from(SMS_ID_SET)
        ); // {sms_id:{orders,allocations}}

        Object.keys(_data).forEach((userId) => {
          for (let i = 0; i < _data[userId].systematicPortfolios.length; i++) {
            let systematicPortfolio: any =
              _data[userId].systematicPortfolios[i];
            const systematicPortfolioId = systematicPortfolio.id;
            systematicPortfolio = {
              ...systematicPortfolio,
              ...SMS_BUFFER[systematicPortfolioId],
            };
            _data[userId].systematicPortfolios[i] = systematicPortfolio;
          }
        });
        const response = await smsAPI.notifyAutoRebalance(
          _data,
          auth.user.additionalCookies
        );
        setReport(response);
        if (
          response?.total_failed === 0 &&
          response?.failed_users.length === 0
        ) {
          feedback.show({
            type: "success",
            message: `Emails sent successfully!`,
          });
        } else {
          setShowReport(true);
          feedback.show({
            type: "warning",
            message: `Could not send ${response?.total_failed} emails... check the report!`,
          });
        }
        setIsSendingEmails(false);
        table.deselectRow(); // deselect All
      } catch (error: any) {
        setIsSendingEmails(false);
        throw new Error(error?.message);
      }
    }
  }, [
    auth.user.additionalCookies,
    data,
    feedback,
    getOrdersAndAllocations,
    smsAPI,
  ]);

  useEffect(() => {
    return () => {
      setData(null);
      setReport(null);
      setShowReport(false);
    };
  }, []);

  useEffect(() => {
    if (data && Object.keys(data).length > 0 && tableReady) {
      const table = tableRef.current.getTableInstance();
      if (tableRef.current) {
        let tableData: any[] = [];
        //prepare table data for tabulator
        for (let key in data) {
          let _data: any = {};
          _data.id = key;
          _data.firstName = data[key].userData.firstName;
          _data.lastName = data[key].userData.lastName;
          _data.email = data[key].userData.email;
          _data.level = data[key].userData.level;
          _data.systematicPortfolios = [];
          data[key].systematicPortfolios.forEach((sp) => {
            const _sp: any = {};
            _sp.name = sp.name;
            _sp.isReadOnly = sp.isReadOnly;
            _data.systematicPortfolios.push(_sp);
          });
          tableData.push(_data);
        }

        if (table.initialized) {
          table.setData(tableData);
          table.redraw(true);
        }
      }
    } else if (tableReady) {
      const table = tableRef.current?.getTableInstance();
      table && table.initialized && table.setData([]);
    }
  }, [data, tableReady]);

  return (
    <Box
      height={"100%"}
      width={"100%"}
      gap={2}
      display={"flex"}
      flexDirection={"column"}
      position={"relative"}
    >
      {isFetchingData ? (
        <Box
          height={"100%"}
          width={"100%"}
          gap={2}
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <CircularProgress color="secondary" />
          <Typography>Loading Data...</Typography>
        </Box>
      ) : data && Object.keys(data).length > 0 ? (
        <>
          <Box display={"flex"} gap={1}>
            <Button
              onClick={sendNotificationHandler}
              disabled={sendBtnDisabled}
              color="secondary"
            >
              Send notifications
            </Button>

            {isSendingEmails && (
              <Box display={"flex"} alignItems={"center"} gap={2}>
                <CircularProgress color="secondary" size={"0.9vw"} />
                <Typography>Sending Notifications...</Typography>
              </Box>
            )}
            {report && (
              <Button onClick={() => setShowReport((prev) => !prev)}>
                {showReport ? "Hide" : "Show"} Report
              </Button>
            )}
          </Box>
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            position={"relative"}
            height={"100%"}
            gap={1}
          >
            <Tabulator
              ref={tableRef}
              options={tableOptions}
              events={tableEvents}
            />
            {showReport && report && (
              <Box
                width={"35%"}
                maxWidth={"40%"}
                display={"flex"}
                height={"100%"}
                overflow={"hidden"}
              >
                <Box
                  gap={1}
                  flexDirection={"column"}
                  display={"flex"}
                  overflow={"hidden"}
                  p={1}
                >
                  <Typography>
                    <strong>Notification Report</strong>
                  </Typography>
                  <Box overflow={"auto"} display={"flex"}>
                    <pre>{JSON.stringify(report, null, 2)}</pre>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </>
      ) : data && Object.keys(data).length === 0 ? (
        <Typography>there is no user to be notified!</Typography>
      ) : null}
    </Box>
  );
}

const _formatAction = (output, value, translate) => {
  var baseClass = "tSystematicProductRebalance";
  var cssOrder = baseClass + "-order";
  var cssBuy = cssOrder + " " + cssOrder + "--buy";
  var cssDecrease = cssOrder + " " + cssOrder + "--decrease";
  var cssIncrease = cssOrder + " " + cssOrder + "--increase";
  var cssSell = cssOrder + " " + cssOrder + "--sell";

  switch (output) {
    case "TEXT": {
      switch (value) {
        case "buy": {
          return translate["c_position_buy"];
        }
        case "decrease": {
          return translate["c_position_decrease"];
        }
        case "increase": {
          return translate["c_position_increase"];
        }
        case "sell": {
          return translate["c_position_sell"];
        }
        // no default
      }
      break;
    }
    // eslint-disable-next-line no-fallthrough
    case "HTML":
    default: {
      switch (value) {
        case "buy": {
          return {
            html:
              '<strong class="' +
              cssBuy +
              '">' +
              translate["c_position_buy"] +
              "</strong>",
          };
        }
        case "decrease": {
          return {
            html:
              '<strong class="' +
              cssDecrease +
              '">' +
              translate["c_position_decrease"] +
              "</strong>",
          };
        }
        case "increase": {
          return {
            html:
              '<strong class="' +
              cssIncrease +
              '">' +
              translate["c_position_increase"] +
              "</strong>",
          };
        }
        case "sell": {
          return {
            html:
              '<strong class="' +
              cssSell +
              '">' +
              translate["c_position_sell"] +
              "</strong>",
          };
        }

        // no default
      }
    }
  }
};
